/** The tenant's own company record and its branch addresses, as React Query hooks over `routers/organization.py`. Both endpoints are gated on `admin` server-side; no client-side gate here, matching `lib/hr.ts`. */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import type { BankAccount } from './types'

export interface Organization {
  id: string
  slug: string
  name_en: string
  name_mn: string
  name_ru: string
  registration_no: string
  phone: string
  email: string
  is_active: boolean
  mail_reply_to: string | null
  is_customer: boolean
}

/** Partial update: only fields sent are applied. `slug` is absent on purpose: it is the tenant's stable key. */
type OrganizationPatch = Partial<
  Pick<
    Organization,
    | 'name_en'
    | 'name_mn'
    | 'name_ru'
    | 'registration_no'
    | 'phone'
    | 'email'
    | 'is_active'
    | 'mail_reply_to'
  >
>

export type AddressKind = 'head_office' | 'branch'
export type AddressLanguage = 'en' | 'mn' | 'ru'

export interface OrganizationAddress {
  id: string
  kind: AddressKind
  label: string
  language: AddressLanguage
  address: string
  phone: string
  email: string
  is_primary: boolean
}

export type AddressDraft = Omit<OrganizationAddress, 'id'>

export function useOrganization() {
  return useQuery({
    queryKey: ['organization'],
    queryFn: async () => (await api.get<Organization>('/api/organization')).data,
  })
}

export function useUpdateOrganization() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (patch: OrganizationPatch) =>
      (await api.put<Organization>('/api/organization', patch)).data,
    onSuccess: (data) => client.setQueryData(['organization'], data),
  })
}

export function useAddresses() {
  return useQuery({
    queryKey: ['organization', 'addresses'],
    queryFn: async () =>
      (await api.get<OrganizationAddress[]>('/api/organization/addresses')).data,
  })
}

export function useCreateAddress() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (draft: AddressDraft) =>
      (await api.post<OrganizationAddress>('/api/organization/addresses', draft)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['organization', 'addresses'] }),
  })
}

export function useUpdateAddress() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<AddressDraft> }) =>
      (await api.put<OrganizationAddress>(`/api/organization/addresses/${id}`, patch)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['organization', 'addresses'] }),
  })
}

export function useDeleteAddress() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/organization/addresses/${id}`)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['organization', 'addresses'] }),
  })
}

/** Which of the four companies issues a document: the letterhead, not the tenant.
 *  ⛔ An identity is not a company row and adding one is not adding a tenant: the four identities are ONE `organization` (CLAUDE.md §1), and a second row makes roster and payroll return zero rows with no error.
 *  Staff, roster and payroll are shared; what differs is document furniture (name, registration and VAT number, seal, bank). No route in this module creates an organization. */
export interface IssuerIdentity {
  id: string
  slug: string
  name_en: string
  name_mn: string
  name_ru: string
  registration_no: string
  vat_registration_no: string
  phone: string
  email: string
  website: string | null
  is_default: boolean
  is_active: boolean
  /** Whether a seal exists to show; the bytes come from `sealImageUrl`. */
  has_seal: boolean
  /** Cache-buster for that image request; null when there is no seal. */
  seal_updated_at: string | null
  /** Whether a logo exists to show; only a customer sets one, org 1's letterheads are built in. */
  has_logo: boolean
  logo_updated_at: string | null
}

/** `slug` is settable once: absent from the patch type deliberately. */
type IssuerIdentityDraft = Omit<
  IssuerIdentity,
  'id' | 'has_seal' | 'seal_updated_at' | 'has_logo' | 'logo_updated_at' | 'slug'
>

type IssuerIdentityCreate = IssuerIdentityDraft & { slug: string }

type IssuerIdentityPatch = Partial<IssuerIdentityDraft>

/** Mirrors `ALLOWED_SEAL_TYPES` / `MAX_SEAL_BYTES`, for an instant client-side message only; the server decodes the bytes rather than trusting the declared type. */
export const ALLOWED_SEAL_TYPES = ['image/png', 'image/jpeg']
export const MAX_SEAL_BYTES = 2 * 1024 * 1024

/** The seal bytes for one identity. ⚠ `seal_updated_at` is a query param on purpose: React Query keys on this string, so without it a replaced seal keeps showing the old image.
 *  ⛔ Not for a bare `<img src>`; see `useIssuerSealImage`. */
function sealImageUrl(identityId: string, updatedAt: string | null): string {
  const bust = updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : ''
  return `/api/organization/identities/${identityId}/seal${bust}`
}

function useObjectUrl(blob: Blob | null | undefined) {
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    if (!blob) {
      setUrl(undefined)
      return
    }
    // Created and revoked in the same effect, so a StrictMode double-mount cannot revoke the URL on screen.
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [blob])
  return url
}

/** One identity's seal as a displayable object URL.
 *  ⛔ Exists because `<img src={sealImageUrl(...)}/>` cannot work: a plain `<img>` sends no Authorization header, so the gated route 401s and renders a broken image silently. The bytes are fetched as a blob, as `useSignatureImage` does.
 *  Keyed on `updatedAt` so a replaced seal fetches fresh bytes. ⚠ `enabled` gates on `hasSeal`, not `updatedAt`: the render condition and the fetch condition must not drift, or a row where they disagree spins forever. */
export function useIssuerSealImage(
  identityId: string,
  hasSeal: boolean,
  updatedAt: string | null,
) {
  const blobQuery = useQuery({
    queryKey: ['organization', 'identities', identityId, 'seal', updatedAt],
    enabled: hasSeal,
    queryFn: async () =>
      (await api.get(sealImageUrl(identityId, updatedAt), { responseType: 'blob' })).data as Blob,
    staleTime: Infinity,
  })

  const url = useObjectUrl(blobQuery.data)

  // `error` is returned so a 403 from the `admin` gate is distinguishable from bytes still arriving.
  return { url, isLoading: blobQuery.isLoading, error: blobQuery.error }
}

/** One identity's logo as a displayable object URL; a plain `<img>` could not send the auth header. */
export function useIssuerLogoImage(
  identityId: string,
  hasLogo: boolean,
  updatedAt: string | null,
) {
  const blobQuery = useQuery({
    queryKey: ['organization', 'identities', identityId, 'logo', updatedAt],
    enabled: hasLogo,
    queryFn: async () => {
      const bust = updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : ''
      const url = `/api/organization/identities/${identityId}/logo${bust}`
      return (await api.get(url, { responseType: 'blob' })).data as Blob
    },
    staleTime: Infinity,
  })
  const url = useObjectUrl(blobQuery.data)
  return { url, isLoading: blobQuery.isLoading, error: blobQuery.error }
}

/** The main company's logo for the side menu, or undefined when it has none.
 *  ⛔ Keyed on the person: the cache outlives a sign-out, and one company must never see another's logo. */
export function useBrandLogo(personId: string | undefined, enabled: boolean) {
  const blobQuery = useQuery({
    queryKey: ['organization', 'brand-logo', personId],
    enabled: enabled && !!personId,
    queryFn: async () => {
      const response = await api.get('/api/organization/brand-logo', { responseType: 'blob' })
      return response.status === 204 ? null : (response.data as Blob)
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  return useObjectUrl(blobQuery.data)
}

export function useIssuerIdentities() {
  return useQuery({
    queryKey: ['organization', 'identities'],
    queryFn: async () =>
      (await api.get<IssuerIdentity[]>('/api/organization/identities')).data,
  })
}

/** Config's list and the sales pickers' `['issuer-identities']` read the same rows through two routes. */
function invalidateIdentities(client: ReturnType<typeof useQueryClient>) {
  client.invalidateQueries({ queryKey: ['organization', 'identities'] })
  client.invalidateQueries({ queryKey: ['issuer-identities'] })
}

export function useCreateIssuerIdentity() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (draft: IssuerIdentityCreate) =>
      (await api.post<IssuerIdentity>('/api/organization/identities', draft)).data,
    onSuccess: () => invalidateIdentities(client),
  })
}

export function useUpdateIssuerIdentity() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: IssuerIdentityPatch }) =>
      (await api.put<IssuerIdentity>(`/api/organization/identities/${id}`, patch)).data,
    onSuccess: () => invalidateIdentities(client),
  })
}

/** Only the two English names; the rest of a bank account is not editable here. */
export function useUpdateBankAccountEnglishNames() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      names,
    }: {
      id: string
      names: { bank_name_en: string | null; account_name_en: string | null }
    }) => (await api.put<BankAccount>(`/api/organization/bank-accounts/${id}`, names)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['bank-accounts'] }),
  })
}

/** Upload or replace one identity's logo; the server appends, so an issued document keeps its own. */
export function useUploadIssuerLogo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)
      return (await api.put<IssuerIdentity>(`/api/organization/identities/${id}/logo`, body))
        .data
    },
    onSuccess: () => {
      invalidateIdentities(client)
      client.invalidateQueries({ queryKey: ['organization', 'brand-logo'] })
    },
  })
}

/** Upload or replace one identity's seal.
 *  ⛔ The server appends: `issuer_seal` is append-only, a replacement repoints `current_seal_id`, and every issued document keeps the seal it froze. Replacing changes only what FUTURE documents carry. */
export function useUploadIssuerSeal() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)
      return (await api.put<IssuerIdentity>(`/api/organization/identities/${id}/seal`, body))
        .data
    },
    onSuccess: () => invalidateIdentities(client),
  })
}
